import { cloneElement, forwardRef, isValidElement, type HTMLAttributes, type ReactElement, type ReactNode, useId } from "react";
import styled from "@emotion/styled";
import type { CSSObject } from "@emotion/react";
import { Label } from "../Label";

export type FormFieldProps = HTMLAttributes<HTMLDivElement> & {
  label?: ReactNode;
  required?: boolean;
  helperText?: ReactNode;
  errorText?: ReactNode;
  disabled?: boolean;
  children: ReactElement;
};

/* Exact compiled output of the pre-migration Tailwind utilities, in the same
 * class-list emission order. Verified against the frozen baseline.
 *
 * Cascade note: on the helper/error <p>, `bfrs-leading-5` [pos 49092] sits
 * after `bfrs-text-sm` [pos 48624] in the compiled stylesheet — both resolve
 * to line-height 1.25rem, so the net value is 1.25rem. */

const FormFieldRoot = styled("div", {
  label: "AgenticFormField-root"
})({
  "& > :not([hidden]) ~ :not([hidden])": {
    "--tw-space-y-reverse": "0",
    marginTop: "calc(.375rem * calc(1 - var(--tw-space-y-reverse)))",
    marginBottom: "calc(.375rem * var(--tw-space-y-reverse))"
  }
});

/* The old code passed `bfrs-block` into Label's className; `&&` reproduces the
 * consumer-class-beats-component-class outcome deterministically. */
const FormFieldLabel = styled(Label, {
  label: "AgenticFormField-label"
})({
  "&&": { display: "block" }
});

const helperBase: CSSObject = {
  fontSize: ".875rem",
  lineHeight: "1.25rem"
};

const FormFieldErrorText = styled("p", {
  label: "AgenticFormField-errorText"
})([
  helperBase,
  {
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-danger) / var(--tw-text-opacity, 1))"
  }
]);

const FormFieldHelperText = styled("p", {
  label: "AgenticFormField-helperText"
})([
  helperBase,
  {
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))"
  }
]);

export const FormField = forwardRef<HTMLDivElement, FormFieldProps>(
  ({ className, label, required, helperText, errorText, disabled, children, ...props }, ref) => {
    const generatedId = useId();
    const controlId = children.props.id ?? generatedId;
    const helperId = helperText ? `${controlId}-helper` : undefined;
    const errorId = errorText ? `${controlId}-error` : undefined;
    const describedBy = [children.props["aria-describedby"], helperId, errorId].filter(Boolean).join(" ") || undefined;

    const control = isValidElement(children)
      ? cloneElement(children, {
          id: controlId,
          disabled: children.props.disabled ?? disabled,
          "aria-describedby": describedBy,
          "aria-invalid": children.props["aria-invalid"] ?? Boolean(errorText)
        })
      : children;

    return (
      <FormFieldRoot ref={ref} className={className} {...props}>
        {label && (
          <FormFieldLabel htmlFor={controlId} required={required} disabled={disabled}>
            {label}
          </FormFieldLabel>
        )}
        {control}
        {errorText ? (
          <FormFieldErrorText id={errorId}>{errorText}</FormFieldErrorText>
        ) : helperText ? (
          <FormFieldHelperText id={helperId}>{helperText}</FormFieldHelperText>
        ) : null}
      </FormFieldRoot>
    );
  }
);

FormField.displayName = "FormField";
